import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ChevronLeft, Store, MessageCircle, Bot, Package, Calendar } from 'lucide-react'
import { createChannelInstance, listChannelInstances, validateTelegramToken } from '@/services/channelsApi'
import { botSettingsApi } from '@/services/botSettingsApi'
import { DEFAULT_REENGAGEMENT_CONFIG } from '@/lib/api/botSettings'
import { normalizeToVertical, getVerticalProfile } from '@/lib/verticalUI'
import api from '@/services/api'
import { useAuthStore } from '@/store/authStore'
import {
  isOnboardingCompleted,
  loadOnboardingDraft,
  persistOnboardingCompleted,
  saveOnboardingDraft,
  setOnboardingCompleted,
} from '@/services/onboardingState'
import type { WizardStep, ChannelType, ValidatedTelegramBot, BotPersona } from './types'
import Step1BusinessInfo from './Step1BusinessInfo'
import Step2ChannelSetup from './Step2ChannelSetup'
import Step3BotConfigSimplified from './Step3BotConfigSimplified'
import Step3b_BusinessProfile from './Step3b_BusinessProfile'
import Step4ProductsOptional from './Step4ProductsOptional'
import Step5Complete from './Step5Complete'
import { ENABLE_UNOFFICIAL_WHATSAPP_QR } from '@/pages/whatsapp/CreateWahaInstanceModal'

export default function OnboardingWizard() {
  const navigate = useNavigate()
  const tenantId = useAuthStore((s) => s.tenantId)
  const user = useAuthStore((s) => s.user)
  const authTenant = useAuthStore((s) => s.tenant)

  const [step, setStep] = useState<WizardStep>(1)
  const [loading, setLoading] = useState(false)
  const [initialLoading, setInitialLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Step 1
  const [storeName, setStoreName] = useState(authTenant?.name || '')
  const [businessType, setBusinessType] = useState(authTenant?.businessType || '')
  const [targetAudience, setTargetAudience] = useState('')

  // Step 2
  const [channelType, setChannelType] = useState<ChannelType>(
    ENABLE_UNOFFICIAL_WHATSAPP_QR ? 'WHATSAPP' : 'WHATSAPP_CLOUD'
  )
  const [instanceName, setInstanceName] = useState('')
  const [botToken, setBotToken] = useState('')
  const [telegramReceiveMode, setTelegramReceiveMode] = useState<'webhook' | 'polling'>('webhook')
  const [cloudPhoneNumberId, setCloudPhoneNumberId] = useState('')
  const [cloudAccessToken, setCloudAccessToken] = useState('')
  const [validatingToken, setValidatingToken] = useState(false)
  const [validatedTelegramBot, setValidatedTelegramBot] = useState<ValidatedTelegramBot | null>(null)
  const [createdChannelId, setCreatedChannelId] = useState<string | null>(null)
  const [existingInstances, setExistingInstances] = useState<Array<{ id: string; channelType: ChannelType; instanceName: string; status: string }>>([])

  // Step 3 (Simplificado)
  const [aiEnabled, setAiEnabled] = useState(true)
  const [botName, setBotName] = useState('Mi Bot')
  const [avatar, setAvatar] = useState('#25D366')
  const [persona, setPersona] = useState<BotPersona>('Friendly')
  const [welcomeMessage, setWelcomeMessage] = useState('¡Hola! 👋 Bienvenido a nuestra tienda. ¿En qué puedo ayudarte hoy?')
  const [typingSimulation, setTypingSimulation] = useState(true)
  const [reengagementEnabled, setReengagementEnabled] = useState(false)

  // Fase A (PLAN-FIX-GAPS-VERTICALES.md): política comercial del tenant
  // — se inicializa con los defaults del vertical en Step 1 y se persiste en Step 3.
  const [commercialPolicyConfig, setCommercialPolicyConfig] = useState<Record<string, unknown> | null>(null)
  const [checkoutConfig, setCheckoutConfig] = useState<Record<string, unknown> | null>(null)

  // Step 4 (Productos)
  const [productsCount, setProductsCount] = useState(0)

  // Step 3b results
  const [faqsCreated, setFaqsCreated] = useState<number>(0)
  const [faqQuestions, setFaqQuestions] = useState<string[]>([])

  useEffect(() => {
    if (!tenantId) {
      setInitialLoading(false)
      return
    }

    let cancelled = false
    const loadAllDataFromDB = async () => {
      try {
        setInitialLoading(true)
        const [tenantRes, botSettingsRes, instancesRes, catalogRes] = await Promise.allSettled([
          api.get('/tenants/current'),
          botSettingsApi.getSettings(),
          listChannelInstances(),
          api.get('/catalog/items?pageSize=1'),
        ])

        if (cancelled) return

        // 1. Datos del Tenant y Profile guardados en BD
        if (tenantRes.status === 'fulfilled' && tenantRes.value.data) {
          const t = tenantRes.value.data
          if (t.name) setStoreName(t.name)
          if (t.businessType) {
            setBusinessType(t.businessType)
            const authState = useAuthStore.getState()
            if (authState.tenant && authState.tenant.businessType !== t.businessType) {
              authState.login({
                token: authState.token!,
                tenantId: authState.tenantId,
                user: authState.user,
                tenant: { ...authState.tenant, businessType: t.businessType },
              })
            }
          }
          if (t.tenantProfile?.targetAudience) setTargetAudience(t.tenantProfile.targetAudience)
          if (t.tenantProfile?.onboardedAt) {
            setOnboardingCompleted(tenantId, true)
          }
          if (Array.isArray(t.channelInstances) && t.channelInstances.length > 0) {
            setExistingInstances(t.channelInstances)
            setCreatedChannelId(t.channelInstances[0].id)
            setChannelType(t.channelInstances[0].channelType as ChannelType)
            setInstanceName(t.channelInstances[0].instanceName)
          }
        } else {
          // Fallback a localStorage si el endpoint no responde
          const draft = loadOnboardingDraft(tenantId)
          if (draft.storeName) setStoreName(draft.storeName)
          if (draft.businessType) setBusinessType(draft.businessType)
          if (draft.targetAudience) setTargetAudience(draft.targetAudience)
        }

        // 2. Canales existentes
        if (instancesRes.status === 'fulfilled' && Array.isArray(instancesRes.value) && instancesRes.value.length > 0) {
          setExistingInstances(instancesRes.value as any)
          if (!createdChannelId) {
            setCreatedChannelId(instancesRes.value[0].id)
            setChannelType(instancesRes.value[0].channelType as ChannelType)
            setInstanceName(instancesRes.value[0].instanceName)
          }
        }

        // 3. Configuración del bot
        if (botSettingsRes.status === 'fulfilled' && botSettingsRes.value) {
          const settings = botSettingsRes.value
          setAiEnabled(!!settings.aiEnabled)
          if (settings.botName) setBotName(settings.botName)
          if (settings.avatar) setAvatar(settings.avatar)
          if (settings.persona) setPersona(settings.persona as BotPersona)
          if (settings.welcomeMessage) setWelcomeMessage(settings.welcomeMessage)
          setTypingSimulation(settings.typingSimulation ?? true)
          if (settings.reengagementConfig?.enabled !== undefined) {
            setReengagementEnabled(settings.reengagementConfig.enabled)
          }
          if (settings.commercialPolicyConfig) {
            setCommercialPolicyConfig(settings.commercialPolicyConfig as Record<string, unknown>)
          }
          if (settings.checkoutConfig) {
            setCheckoutConfig(settings.checkoutConfig as unknown as Record<string, unknown>)
          }
        }

        // 4. Conteo real de productos en BD
        if (catalogRes.status === 'fulfilled' && catalogRes.value.data) {
          const total = catalogRes.value.data.total ?? catalogRes.value.data.items?.length ?? 0
          setProductsCount(total)
        }
      } catch (err) {
        console.warn('Error cargando datos de onboarding desde BD:', err)
      } finally {
        if (!cancelled) setInitialLoading(false)
      }
    }

    loadAllDataFromDB()
    return () => {
      cancelled = true
    }
  }, [tenantId])

  const normalizedVertical = normalizeToVertical(businessType)
  const verticalProfile = getVerticalProfile(normalizedVertical)
  const isAppointmentVertical = !verticalProfile.operational.hasFulfillment

  const stepConfig = [
    { number: 1, title: 'Información básica', icon: Store, color: 'blue', time: '1 min' },
    { number: 2, title: 'Conectar canal', icon: MessageCircle, color: 'green', time: '2 min' },
    { number: 3, title: 'Configurar bot', icon: Bot, color: 'purple', time: '2 min' },
    { number: 4, title: 'Perfil del negocio', icon: Store, color: 'amber', time: 'Opcional' },
    {
      number: 5,
      title: verticalProfile.terminology.itemsPluralName,
      icon: isAppointmentVertical ? Calendar : Package,
      color: 'orange',
      time: 'Opcional',
    },
    { number: 6, title: 'Finalizar', icon: Store, color: 'amber', time: '1 min' },
  ]

  const renderStep3b = () => (
    <Step3b_BusinessProfile
      businessType={businessType}
      targetAudience={targetAudience}
      channelInstanceId={createdChannelId}
      initialCommercialPolicy={commercialPolicyConfig as any}
      onCommercialPolicyChange={(policy) => setCommercialPolicyConfig(policy as unknown as Record<string, unknown>)}
      onBack={() => setStep(3)}
      onSkip={() => {
        setStep(5)
      }}
      onNext={(result) => {
        setFaqsCreated(result?.faqsCreated || 0)
        setFaqQuestions(result?.faqQuestions || [])
        setStep(5)
      }}
    />
  )

  const markCompletedAndGo = (path: '/app/dashboard' | '/app/instances') => {
    setOnboardingCompleted(tenantId, true)
    saveOnboardingDraft(tenantId, { storeName, businessType, targetAudience })
    if (tenantId) {
      persistOnboardingCompleted(tenantId).catch((err) => {
        console.warn('Error persistiendo estado de onboarding en servidor:', err)
      })
    }
    navigate(path, { replace: true })
  }

  const handleSkip = () => {
    markCompletedAndGo('/app/dashboard')
  }

  const handleStep1Next = async () => {
    setError(null)
    if (!storeName.trim()) {
      setError('Ingresa el nombre de la tienda para continuar.')
      return
    }

    const draft = {
      storeName: storeName.trim(),
      businessType: businessType.trim(),
      targetAudience: targetAudience.trim(),
    }
    saveOnboardingDraft(tenantId, draft)

    try {
      setLoading(true)
      await api.patch('/tenants/me', {
        name: draft.storeName,
        businessType: draft.businessType || undefined,
      })

      // Actualizar el nombre y vertical del tenant en authStore para que la interfaz se sincronice inmediatamente
      const authState = useAuthStore.getState()
      if (authState.tenant) {
        authState.login({
          token: authState.token!,
          tenantId: authState.tenantId,
          user: authState.user,
          tenant: { ...authState.tenant, name: draft.storeName, businessType: draft.businessType },
        })
      }

      if (draft.targetAudience) {
        await api.post(`/api/tenants/${tenantId}/profile`, {
          targetAudience: draft.targetAudience,
        })
      }
      setStep(2)
    } catch (err: any) {
      setError(err?.response?.data?.error || 'No se pudo guardar la información del negocio.')
    } finally {
      setLoading(false)
    }
  }

  const handleValidateTelegramToken = async () => {
    if (!botToken.trim()) {
      setError('Ingresa el token de Telegram antes de validar.')
      return
    }
    setError(null)
    setValidatedTelegramBot(null)
    setValidatingToken(true)
    try {
      const result = await validateTelegramToken(botToken.trim())
      if (!result.valid || !result.bot) {
        setError(result.error || 'No se pudo validar el token.')
        return
      }
      setValidatedTelegramBot(result.bot)
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Token inválido de Telegram.')
    } finally {
      setValidatingToken(false)
    }
  }

  const handleCreateChannel = async () => {
    setError(null)
    if (!instanceName.trim()) {
      setError('Ingresa el nombre de la instancia.')
      return
    }

    const payload: {
      channelType: ChannelType
      instanceName: string
      config?: Record<string, any>
    } = {
      channelType,
      instanceName: instanceName.trim(),
      config: {},
    }

    if (channelType === 'TELEGRAM') {
      if (!botToken.trim()) {
        setError('El bot token es requerido para Telegram.')
        return
      }
      payload.config = {
        botToken: botToken.trim(),
        receiveMode: telegramReceiveMode,
      }
    } else if (channelType === 'WHATSAPP_CLOUD') {
      if (!cloudPhoneNumberId.trim() || !cloudAccessToken.trim()) {
        setError('Phone Number ID y Access Token son requeridos para Cloud API.')
        return
      }
      payload.config = {
        phoneNumberId: cloudPhoneNumberId.trim(),
        accessToken: cloudAccessToken.trim(),
      }
    }

    try {
      setLoading(true)
      const instance = await createChannelInstance(payload)
      setCreatedChannelId(instance.id)
      setStep(3)
    } catch (err: any) {
      setError(err?.response?.data?.error || 'No se pudo crear el canal.')
    } finally {
      setLoading(false)
    }
  }

  const handleSaveBotStep = async () => {
    setError(null)
    try {
      setLoading(true)
      await botSettingsApi.updateSettings({
        aiEnabled,
        botName,
        avatar,
        persona,
        welcomeMessage,
        typingSimulation,
        reengagementConfig: {
          ...DEFAULT_REENGAGEMENT_CONFIG,
          enabled: reengagementEnabled,
        },
        // Fase A (PLAN-FIX-GAPS-VERTICALES.md): persistir política comercial
        // y logística de despacho en el onboarding, no después.
        commercialPolicyConfig,
        ...(checkoutConfig ? { checkoutConfig } : {}),
      } as any)
      setStep(4)
    } catch {
      setError('No se pudo guardar la configuración del bot.')
    } finally {
      setLoading(false)
    }
  }

  const handleSkipProducts = () => {
    setStep(6)
  }

  const handleProductsNext = () => {
    setStep(6)
  }

  const handleContinueWithExisting = () => {
    if (createdChannelId) {
      setStep(3)
    } else if (existingInstances.length > 0) {
      setCreatedChannelId(existingInstances[0].id)
      setChannelType(existingInstances[0].channelType as ChannelType)
      setInstanceName(existingInstances[0].instanceName)
      setStep(3)
    }
  }

  const handleSelectExistingInstance = (id: string) => {
    const inst = existingInstances.find((i) => i.id === id)
    if (inst) {
      setCreatedChannelId(inst.id)
      setChannelType(inst.channelType as ChannelType)
      setInstanceName(inst.instanceName)
    }
  }

  return (
    <div className="min-h-screen bg-[#f6f8f8] flex flex-col">
      {/* Sticky header */}
      <header className="w-full bg-[#f6f8f8] sticky top-0 z-10">
        <div className="flex items-center px-4 py-4 justify-between max-w-lg mx-auto w-full">
          <button
            onClick={step > 1 ? () => setStep((step - 1) as WizardStep) : handleSkip}
            className="text-[#075f55] flex items-center justify-center p-2 rounded-full hover:bg-[#075f55]/10 transition-colors"
          >
            <ChevronLeft className="h-5 w-5" />
          </button>
          <div className="flex-1 text-center">
            <h2 className="text-slate-900 text-lg font-bold leading-tight">
              Configuración inicial
            </h2>
            <p className="text-xs font-semibold text-[#075f55]/80">
              Paso {step} de 6: {stepConfig[step - 1]?.title}
            </p>
          </div>
          <button
            type="button"
            onClick={handleSkip}
            className="text-sm font-medium text-[#075f55] hover:underline px-2 py-1"
          >
            Omitir
          </button>
        </div>
        {/* Interactive Stepper */}
        <div className="flex w-full flex-row items-center justify-center gap-3 py-3 border-b border-[#075f55]/10">
          {stepConfig.map((s) => (
            <button
              key={s.number}
              type="button"
              onClick={() => setStep(s.number as WizardStep)}
              title={`Paso ${s.number}: ${s.title}`}
              className={`rounded-full transition-all duration-300 cursor-pointer focus:outline-none focus:ring-2 focus:ring-[#075f55]/30 ${
                step === s.number
                  ? 'h-2.5 w-8 bg-[#075f55]'
                  : step > s.number
                  ? 'h-2.5 w-2.5 bg-[#075f55] hover:scale-125'
                  : 'h-2.5 w-2.5 bg-[#075f55]/20 hover:scale-125'
              }`}
            />
          ))}
        </div>
      </header>

      {/* Content */}
      <main className="flex-1 w-full max-w-lg mx-auto px-6 pt-8 pb-12 overflow-y-auto">
        {initialLoading ? (
          <div className="flex flex-col items-center justify-center py-24 gap-3">
            <div className="h-9 w-9 animate-spin rounded-full border-3 border-[#075f55] border-t-transparent" />
            <p className="text-sm font-medium text-slate-600">Cargando datos guardados...</p>
          </div>
        ) : (
          <>
            {step === 1 && (
              <Step1BusinessInfo
                storeName={storeName}
                businessType={businessType}
                targetAudience={targetAudience}
                userName={user?.name}
                onStoreNameChange={setStoreName}
                onBusinessTypeChange={setBusinessType}
                onTargetAudienceChange={setTargetAudience}
                onNext={handleStep1Next}
              />
            )}

            {step === 2 && (
              <Step2ChannelSetup
                channelType={channelType}
                instanceName={instanceName}
                botToken={botToken}
                telegramReceiveMode={telegramReceiveMode}
                cloudPhoneNumberId={cloudPhoneNumberId}
                cloudAccessToken={cloudAccessToken}
                validatingToken={validatingToken}
                validatedTelegramBot={validatedTelegramBot}
                loading={loading}
                existingInstances={existingInstances}
                selectedExistingId={createdChannelId}
                onSelectExistingInstance={handleSelectExistingInstance}
                onContinueWithExisting={handleContinueWithExisting}
                onChannelTypeChange={setChannelType}
                onInstanceNameChange={setInstanceName}
                onBotTokenChange={setBotToken}
                onTelegramReceiveModeChange={setTelegramReceiveMode}
                onCloudPhoneNumberIdChange={setCloudPhoneNumberId}
                onCloudAccessTokenChange={setCloudAccessToken}
                onValidateTelegramToken={handleValidateTelegramToken}
                onBack={() => setStep(1)}
                onNext={handleCreateChannel}
              />
            )}

            {step === 3 && (
              <Step3BotConfigSimplified
                aiEnabled={aiEnabled}
                botName={botName}
                avatar={avatar}
                persona={persona}
                welcomeMessage={welcomeMessage}
                typingSimulation={typingSimulation}
                businessType={businessType}
                commercialPolicyConfig={commercialPolicyConfig}
                checkoutConfig={checkoutConfig}
                reengagementEnabled={reengagementEnabled}
                onReengagementEnabledChange={setReengagementEnabled}
                onCommercialPolicyChange={setCommercialPolicyConfig}
                onCheckoutConfigChange={setCheckoutConfig}
                onAiEnabledChange={setAiEnabled}
                onBotNameChange={setBotName}
                onAvatarChange={setAvatar}
                onPersonaChange={setPersona}
                onWelcomeMessageChange={setWelcomeMessage}
                onTypingSimulationChange={setTypingSimulation}
                onBack={() => setStep(2)}
                onNext={handleSaveBotStep}
              />
            )}

            {step === 4 && renderStep3b()}

            {step === 5 && (
              <Step4ProductsOptional
                productsCount={productsCount}
                businessType={businessType}
                onProductCreated={() => setProductsCount((c) => c + 1)}
                onSkip={handleSkipProducts}
                onNext={handleProductsNext}
                onBack={() => setStep(4)}
              />
            )}

            {step === 6 && (
              <Step5Complete
                storeName={storeName}
                businessType={businessType}
                channelType={channelType}
                instanceName={instanceName}
                createdChannelId={createdChannelId}
                botName={botName}
                aiEnabled={aiEnabled}
                productsCount={productsCount}
                faqsCreated={faqsCreated}
                faqQuestions={faqQuestions}
                onGoToInstances={() => markCompletedAndGo('/app/instances')}
                onGoToDashboard={() => markCompletedAndGo('/app/dashboard')}
              />
            )}

            {error && (
              <div className="mt-4 bg-red-50 border-l-4 border-red-400 p-4 rounded-r-lg">
                <div className="flex gap-3">
                  <span className="flex-shrink-0 text-xl">❌</span>
                  <div>
                    <h5 className="text-sm font-semibold text-red-900 mb-1">Error</h5>
                    <p className="text-sm text-red-800">{error}</p>
                  </div>
                </div>
              </div>
            )}

            {!tenantId && (
              <div className="mt-4 bg-red-50 border border-red-200 rounded-lg p-4">
                <p className="text-sm text-red-800 font-medium">
                  ⚠️ No se detectó tenant activo. Por favor, vuelve a iniciar sesión.
                </p>
              </div>
            )}

            {isOnboardingCompleted(tenantId) && step < 6 && (
              <div className="mt-3 bg-[#075f55]/5 border border-[#075f55]/10 rounded-lg p-3">
                <p className="text-xs text-[#075f55]">
                  ℹ️ Este tenant ya completó el onboarding. Puedes revisar o actualizar la configuración aquí.
                </p>
              </div>
            )}

            <p className="mt-8 text-center text-xs text-slate-400">Paso {step} de 6 • IridiaChat Onboarding</p>
          </>
        )}
      </main>
    </div>
  )
}
